import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import StripePayment from "../../common/components/PaymentStep/StripePayment";
import NoPayment from "../../common/components/PaymentStep/NoPayment";
import InitializationErrorInfo from "../../common/components/PaymentStep/InitializationErrorInfo";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { useAppDispatch } from "../../../../redux/useDispatch";
import {
  bookingCreationCompleted,
  initializePaymentProvider,
  paymentInitializationRetryStarted,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { RootState } from "../../../../redux/store";
import routes from "../../../../routes/routes";
import { parseBookingQuery } from "../common/url/bookingQuery";
import { calculateDisplayPrice, findCoupons } from "../../../../util/payments";

/**
 * KeyHandoverPaymentPage Component
 * ---------------------------------------------------------------
 * Handles the payment step for key handover bookings in the route-based flow.
 *
 * @remarks
 * This page:
 * - Initializes payment provider (Stripe or no_payment based on price)
 * - Displays payment UI (StripePayment or NoPayment component)
 * - Redirects to orders page after successful payment
 * - Handles payment initialization errors with retry option
 * - Guards against missing prerequisites (location, service, reservation)
 *
 * @example
 * Route: /key-handover/payment?locationId=loc-123&serviceId=svc-456&...
 */
const KeyHandoverPaymentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const paymentProvider = useSelector((state: RootState) => state.bookingCreation.payment.provider);
  const paymentInitFailed = useSelector((state: RootState) => state.bookingCreation.payment.paymentInitFailed);
  const stripePayment = useSelector((state: RootState) => state.bookingCreation.payment.stripe);
  const orderIds = useSelector((state: RootState) => state.bookingCreation.payment.orderIds);
  const reservations = useSelector((state: RootState) => state.bookingCreation.reservations);

  const [isLoading, setIsLoading] = useState(true);

  // Calculate charge price from reservations
  const totalPrice = reservations?.[0]?.payment.totalPrice ?? 0;
  const chargePrice = calculateDisplayPrice(totalPrice, findCoupons(reservations || []));

  // Guard: require location & service in URL
  useEffect(() => {
    const query = parseBookingQuery(location.search);

    if (!query.locationId) {
      navigate({ pathname: routes.keyHandoverLocationSelectionPath, search: location.search }, { replace: true });
      return;
    }

    if (!query.serviceId) {
      navigate({ pathname: routes.keyHandoverServiceSelectionPath, search: location.search }, { replace: true });
      return;
    }

    // Guard: require reservation to exist before payment
    if (!reservations || reservations.length === 0) {
      navigate({ pathname: routes.keyHandoverSummaryPath, search: location.search }, { replace: true });
    }
  }, [location.search, navigate, reservations]);

  // Redirect to orders after successful payment
  useEffect(() => {
    if (orderIds?.length) {
      navigate(routes.orders);
      setTimeout(() => dispatch(bookingCreationCompleted()), 1000);
    }
  }, [orderIds, dispatch, navigate]);

  // Initialize payment provider
  useEffect(() => {
    if (chargePrice === 0) {
      dispatch(initializePaymentProvider("no_payment"));
    } else {
      dispatch(initializePaymentProvider("stripe"));
    }
    setIsLoading(false);
  }, [chargePrice, dispatch]);

  // Loading state
  if (isLoading || !paymentProvider) {
    return <LoaderOverlay open={true} />;
  }

  // Error state with retry - navigates back to summary page
  if (paymentProvider !== "no_payment" && paymentInitFailed) {
    const showTryAgain = Boolean(
      paymentProvider === "stripe" && stripePayment.token && stripePayment?.checkoutFrontendUrl
    );

    return (
      <KeyHandoverPageLayout
        headerText={t("book_location.payment.header_text", {
          defaultValue: "Payment",
        })}
        backTo={routes.keyHandoverSummaryPath}
      >
        <KeyHandoverContentWrapper>
          <InitializationErrorInfo
            showTryAgain={showTryAgain}
            onBackClick={() => {
              navigate({
                pathname: routes.keyHandoverSummaryPath,
                search: location.search
              });
            }}
            onTryAgainClick={() => dispatch(paymentInitializationRetryStarted({}))}
          />
        </KeyHandoverContentWrapper>
      </KeyHandoverPageLayout>
    );
  }

  // Render payment UI
  if (paymentProvider === "stripe") {
    return <StripePayment service="keys" />;
  } else if (paymentProvider === "no_payment") {
    return <NoPayment service="keys" />;
  }

  return <LoaderOverlay open={true} />;
};

export default KeyHandoverPaymentPage;
